import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Coffee, Heart, Mail, MessageCircle } from 'lucide-react';
import { useSiteSettings } from '../services/siteSettings';

// Custom SVG za X (Twitter) jer ga Lucide-react nema uvek u bazi kao novi logo
const XIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.932zm-1.292 19.49h2.039L6.486 3.24H4.298L17.61 20.643z" />
  </svg>
);

const FooterContainer = styled.footer`
  background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%);
  border-top: 1px solid #333333;
  padding: 60px 0 40px;
  @media (max-width: 768px) { padding: 40px 0 30px; }
`;

const FooterContent = styled.div`
  max-width: 1200px; margin: 0 auto; padding: 0 20px;
`;

const FooterTop = styled.div`
  display: grid; 
  grid-template-columns: 2fr 1fr 1fr; 
  gap: 40px; margin-bottom: 40px;
  @media (max-width: 992px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 576px) { grid-template-columns: 1fr; gap: 30px; text-align: center; }
`;

const FooterSection = styled.div`
  h3 {
    background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
    font-size: 1.25rem; font-weight: 700; font-family: var(--font-heading); margin-bottom: 20px;
    display: flex; align-items: center; 
    @media (max-width: 576px) { justify-content: center; }
  }
  p { color: #aaaaaa; line-height: 1.6; margin-bottom: 16px; font-family: var(--font-body); }
`;

const FooterLinks = styled.ul`
  list-style: none; padding: 0;
  li {
    margin-bottom: 12px;
    a { color: #aaaaaa; text-decoration: none; font-family: var(--font-body); transition: color 0.3s ease; &:hover { color: #e0c16f; } }
  }
`;

const FooterBottom = styled.div`
  display: flex; align-items: center; justify-content: space-between; padding-top: 30px; border-top: 1px solid #222222;
  @media (max-width: 576px) { flex-direction: column; gap: 20px; text-align: center; }
`;

const Copyright = styled.p`color: #888888; font-size: 0.875rem; font-family: var(--font-body); margin: 0;`;

const SocialLinks = styled.div`
  display: flex; align-items: center; gap: 12px;
  @media (max-width: 576px) { justify-content: center; }
`;

const SocialLink = styled.a`
  display: flex; align-items: center; justify-content: center; width: 40px; height: 40px; background: #111111; border: 1px solid #333333; border-radius: 8px; color: #cccccc; text-decoration: none; transition: all 0.3s ease;
  &:hover { background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); color: #000000; border-color: #e0c16f; transform: translateY(-2px); }
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const site = useSiteSettings();

  return (
    <FooterContainer>
      <FooterContent>
        <FooterTop>
          <FooterSection>
            <h3>
              <Coffee size={20} style={{ marginRight: '8px', color: '#e0c16f' }} /> BUCX
            </h3>
            <p>Empowering creators to build sustainable income streams and meaningful connections with their supporters.</p>
            <p>Made with love <Heart size={16} style={{ color: '#ff4757', verticalAlign: 'middle', margin: '0 4px' }} /> by{' '}
              <a href="https://bucx.io" target="_blank" rel="noopener noreferrer" style={{ color: '#e0c16f', textDecoration: 'none', fontWeight: 'bold' }}>BUCX.io</a>
            </p>
          </FooterSection>
          <FooterSection>
            <h3>Quick Links</h3>
            <FooterLinks>
              <li><Link to="/">Home</Link></li>
              <li><Link to="/creators">Explore Creators</Link></li>
            </FooterLinks>
          </FooterSection>
          <FooterSection>
            <h3>Legal</h3>
            <FooterLinks>
              <li><Link to="/privacy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms of Service</Link></li>
              <li><Link to="/cookies">Cookie Policy</Link></li>
              <li><Link to="/refunds">Refund Policy</Link></li>
            </FooterLinks>
          </FooterSection>
        </FooterTop>
        <FooterBottom>
          <Copyright>© {currentYear} BUCX. All rights reserved.</Copyright>
          <SocialLinks>
            {site.twitter_url && <SocialLink href={site.twitter_url} target="_blank" rel="noreferrer" aria-label="X (Twitter)">
              <XIcon />
            </SocialLink>}
            {site.discord_url && <SocialLink href={site.discord_url} target="_blank" rel="noreferrer" aria-label="Discord">
              <MessageCircle size={20}/>
            </SocialLink>}
            <SocialLink href={`mailto:${site.support_email}`} aria-label="Email">
              <Mail size={20}/>
            </SocialLink>
          </SocialLinks>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;
